/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components/RecordingManagementActions.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Action buttons component for Recording Management DataGrid rows.
────────────────────────────────────────────────────────────────*/

import { Play, Pause, Download, Edit, Trash2 } from 'lucide-react';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';
import { VoiceRecording } from '../types';

interface RecordingManagementActionsProps {
  recording: VoiceRecording;
  isCurrentlyPlaying: boolean;
  isPaused: boolean;
  onPlay: () => void;
  onDownload: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

/**
 * Render action buttons for a recording row
 */
export function RecordingManagementActions({
  recording,
  isCurrentlyPlaying,
  isPaused,
  onPlay,
  onDownload,
  onEdit,
  onDelete,
}: RecordingManagementActionsProps) {
  const { t } = useI18n();

  const isPausedForThis = isCurrentlyPlaying && isPaused;

  return (
    <div className="flex items-center gap-1">
      <SimpleTooltip
        content={
          isPausedForThis
            ? t('recordingManagement.actions.play')
            : isCurrentlyPlaying
              ? t('recordingManagement.actions.pause')
              : t('recordingManagement.actions.play')
        }
      >
        <button
          onClick={onPlay}
          className={`p-1.5 rounded transition-colors ${
            isCurrentlyPlaying
              ? 'text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:hover:text-blue-100 dark:hover:bg-blue-900/40'
              : 'text-muted-foreground hover:text-blue-600 hover:bg-blue-50 dark:hover:text-blue-200 dark:hover:bg-blue-900/40'
          }`}
        >
          {isPausedForThis ? (
            <Play className="h-4 w-4" />
          ) : isCurrentlyPlaying ? (
            <Pause className="h-4 w-4" />
          ) : (
            <Play className="h-4 w-4" />
          )}
        </button>
      </SimpleTooltip>

      <SimpleTooltip content={t('recordingManagement.actions.download')}>
        <button
          onClick={onDownload}
          className="p-1.5 rounded transition-colors text-slate-600 hover:text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:text-slate-100 dark:hover:bg-slate-900/40"
        >
          <Download className="h-4 w-4" />
        </button>
      </SimpleTooltip>

      <SimpleTooltip content={t('recordingManagement.actions.edit')}>
        <button
          onClick={onEdit}
          className="p-1.5 rounded transition-colors text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-200 dark:hover:bg-blue-900/40"
        >
          <Edit className="h-4 w-4" />
        </button>
      </SimpleTooltip>

      <SimpleTooltip content={t('recordingManagement.actions.delete')}>
        <button
          onClick={onDelete}
          className="p-1.5 rounded transition-colors text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-200 dark:hover:bg-red-900/40"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </SimpleTooltip>
    </div>
  );
}

